import { useMutation } from '@apollo/client';
import { UserIcon } from 'lucide-react';

import { cacheUtils, useCompleteFragment } from '@eluve/apollo-client';
import {
  Box,
  DetailsSidebar,
  Divider,
  H4,
  HStack,
  SingleDateSelector,
  TimeRangeSelect,
  VStack,
  useToast,
} from '@eluve/components';
import {
  AppointmentPatientIdFragment,
  patientFragment,
  useAppointmentStatus,
  useExternalAppointmentId,
  useIsAppointmentReadonly,
  useLatestSuperbill,
} from '@eluve/frontend-appointment-hooks';
import { SessionTypeSelector } from '@eluve/frontend-feature-appointment';
import { VariablesOf, graphql } from '@eluve/graphql.tada';
import {
  FeatureFlaggedComponent,
  LocationSelector,
  PatientSelector,
  useIsFeatureFlagEnabledForLocation,
  useUserName,
} from '@eluve/smart-blocks';

import { EhrDataImporter } from '../../components/EhrDataImporter';

import { AppointmentDetailsChiefComplaintReasonForVisit } from './AppointmentDetailsChiefComplaintReasonForVisit';
import { SuperbillCard } from './SuperbillCard';

interface AppointmentDetailsProps {
  tenantId: string;
  appointmentId: string;
}

export const AppointmentDetailsFragment = graphql(`
  fragment AppointmentDetails on Appointments @_unmask {
    __typename
    id
    startedAt
    startDate
    endDate
    locationId
    patientId
    userId
    inputLanguage
    outputLanguage
  }
`);

const UpdateAppointmentDetailsMutation = graphql(
  `
    mutation UpdateAppointmentDetails(
      $tenantId: uuid!
      $id: uuid!
      $input: AppointmentsSetInput!
    ) {
      updateAppointmentsByPk(
        pkColumns: { tenantId: $tenantId, id: $id }
        _set: $input
      ) {
        ...AppointmentDetails
        patient {
          ...patient
        }
      }
    }
  `,
  [AppointmentDetailsFragment, patientFragment],
);

const toDate = (date?: string | null) => (date ? new Date(date) : undefined);
const dateToISOString = (date?: Date | null) =>
  date ? date.toISOString() : null;

export const AppointmentDetails: React.FC<AppointmentDetailsProps> = ({
  tenantId,
  appointmentId,
}) => {
  const externalAppointmentId = useExternalAppointmentId();
  const isAppointmentReadonly = useIsAppointmentReadonly();
  const toast = useToast();

  const appointmentDetails = useCompleteFragment({
    fragment: AppointmentDetailsFragment,
    key: {
      id: appointmentId,
    },
  });

  const { downloadUrl, superbill } = useLatestSuperbill();
  const showArtifacts = superbill != null;

  const isAppointmentDetailsReadonly = useIsFeatureFlagEnabledForLocation(
    'APPOINTMENT_DETAILS_READONLY',
    appointmentDetails.locationId ?? '',
  );

  const isReadonly =
    isAppointmentReadonly ||
    (isAppointmentDetailsReadonly && Boolean(externalAppointmentId));

  const username = useUserName(appointmentDetails?.userId ?? '');
  const status = useAppointmentStatus();

  const [updateAppointmentDetails] = useMutation(
    UpdateAppointmentDetailsMutation,
    {
      optimisticResponse: ({ input, id }) => {
        return {
          ___typename: 'mutation_root' as const,
          updateAppointmentsByPk: {
            ...appointmentDetails,
            ...input,
            startDate: input.startDate ?? appointmentDetails.startDate,
            inputLanguage: appointmentDetails.inputLanguage ?? '',
            outputLanguage: appointmentDetails.outputLanguage ?? '',
            id,
            patient: null,
          },
        };
      },
      onError: () => {
        toast.error('Unable to update appointment details');
      },
      update: (_, { data }) => {
        const patientId = data?.updateAppointmentsByPk?.patientId;
        if (!patientId) {
          return;
        }

        cacheUtils.updateFragment(
          {
            fragment: AppointmentPatientIdFragment,
            key: { id: appointmentId },
          },
          (existing) => {
            if (!existing) {
              return;
            }
            return {
              ...existing,
              patientId,
            };
          },
        );
      },
    },
  );

  const handleChange = (
    input: VariablesOf<typeof UpdateAppointmentDetailsMutation>['input'],
  ) => {
    updateAppointmentDetails({
      variables: {
        tenantId,
        id: appointmentId,
        input,
      },
    });
  };

  return (
    <DetailsSidebar title="Details">
      <VStack className="p-4">
        <VStack gap={4}>
          <PatientSelector
            disabled={isReadonly}
            tenantId={tenantId}
            onPatientSelected={(patientId) =>
              handleChange({ patientId: patientId ?? null })
            }
          />

          <FeatureFlaggedComponent flag="APPOINTMENT_COMPLAINT_REASON_ENABLED">
            <AppointmentDetailsChiefComplaintReasonForVisit
              appointmentId={appointmentId}
              tenantId={tenantId}
            />
          </FeatureFlaggedComponent>
        </VStack>

        <VStack gap={4} className="px-2 pt-4 lg:px-0">
          <div className="px-2 lg:px-0">
            <H4>Appointment</H4>
          </div>
          <Box className="grid w-full grid-cols-[auto] items-center gap-4 text-sm lg:grid-cols-[max-content_1fr]">
            <span className="px-2 text-text-tertiary lg:px-0">Date</span>
            <SingleDateSelector
              disabled={isReadonly}
              date={toDate(appointmentDetails?.startDate)}
              onDateChange={(newDate) => {
                if (newDate) {
                  handleChange({ startDate: dateToISOString(newDate) });
                }
              }}
              variant="subtle"
            />

            <span className="px-2 text-text-tertiary lg:px-0">Time</span>
            <TimeRangeSelect
              disabled={isReadonly}
              date={toDate(appointmentDetails?.startDate)}
              startTime={toDate(appointmentDetails?.startDate)}
              endTime={toDate(appointmentDetails?.endDate)}
              onStartTimeChange={(newTime) => {
                if (newTime) {
                  handleChange({ startDate: dateToISOString(newTime) });
                }
              }}
              onEndTimeChange={(newTime) => {
                if (newTime) {
                  handleChange({ endDate: dateToISOString(newTime) });
                }
              }}
              variant="subtle"
            />

            <span className="px-2 text-text-tertiary lg:px-0">Location</span>
            <LocationSelector
              disabled={isReadonly}
              tenantId={tenantId}
              selectedLocationId={appointmentDetails?.locationId ?? undefined}
              onLocationSelected={(location) =>
                handleChange({ locationId: location?.id ?? null })
              }
              variant="subtle"
            />

            <FeatureFlaggedComponent flag="SESSION_TYPES">
              <span className="mb-2 px-2 text-text-tertiary lg:px-0">Type</span>
              <SessionTypeSelector disabled={isReadonly} />
            </FeatureFlaggedComponent>

            <span className="px-2 text-text-tertiary lg:px-0">Owner</span>
            <HStack className="overflow-hidden px-2" title={username}>
              <UserIcon size={16} />
              <p className="flex-1 truncate text-left text-sm">{username}</p>
            </HStack>
          </Box>
        </VStack>

        {showArtifacts && <Divider className="mt-4" />}

        {showArtifacts && (
          <VStack gap={4} className="px-2 lg:px-0">
            <div className="px-2 lg:px-0">
              <H4>Artifacts</H4>
            </div>
            <SuperbillCard
              createdAt={superbill.createdAt}
              downloadUrl={downloadUrl}
            />
          </VStack>
        )}

        {status !== 'NOT_STARTED' && (
          <>
            <div className="flex-1" />
            <EhrDataImporter onlyShowIfError />
          </>
        )}
      </VStack>
    </DetailsSidebar>
  );
};
